import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  input,
  OnChanges,
  OnDestroy,
  Signal,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ActionEvent,
  CheckState,
  CommonUtils,
  ScreenSwipeDirection,
  StandardAction,
  StandardActionBuilder,
  StandardListEntity,
  SwipeDirective,
  SwitchAction,
  SwitchActionBuilder,
} from '@infodata-group/inf-commons';
import {
  InputSearchComponent,
  List2Component,
  SlidingMenuComponent,
  StandardInput,
} from '@infodata-group/inf-commons-mobile';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';

const BACK_ACTION_TEXT = 'Retour';
const DEFAULT_SLIDING_MENU_TITLE = 'Sélection';
const PIXEL_BY_CHARACTER = 30;
const SEARCH_ACTION = new SwitchActionBuilder('search', 'search')
  .setAlign('right')
  .setIconColor(CommonUtils.color('link'))
  .build();
const OK_ACTION = new StandardActionBuilder('ok', '')
  .setText('i18n.common.OK')
  .setAlign('right')
  .setBold(true)
  .build();
const ANIMATION_DURATION = 300;

/**
 * @Deprecated : Waiting for sliding list component from commons mobile
 */
@Component({
  selector: 'infl-sliding-list',
  imports: [
    CommonModule,
    SlidingMenuComponent,
    SwipeDirective,
    InputSearchComponent,
    List2Component,
    TranslatePipe,
  ],
  standalone: true,
  templateUrl: './sliding-list.component.html',
  styleUrl: './sliding-list.component.scss',
})
export class SlidingListComponent<T extends StandardListEntity>
  extends StandardInput<T | T[] | string | string[]>
  implements OnDestroy, OnChanges
{
  private readonly subs: Subscription[] = [];

  public src$ = input.required<Observable<T[]>>();
  public isMultiple = input<boolean>(false);
  public withSearch = input<boolean>(true);

  private _defaultValueString = computed(() => {
    const defaultValue = this.defaultValue();
    if (typeof defaultValue === 'string') {
      return [defaultValue];
    }
    if (Array.isArray(defaultValue)) {
      if (typeof defaultValue.at(0) === 'string') {
        return defaultValue as string[];
      }
    }
    return [];
  });
  private _defaultValueT = computed(() => {
    const defaultValue = this.defaultValue();
    if (defaultValue instanceof StandardListEntity) {
      return [defaultValue] as T[];
    }
    if (Array.isArray(defaultValue)) {
      if (defaultValue.at(0) instanceof StandardListEntity) {
        return defaultValue as T[];
      }
    }
    return [];
  });

  private items = signal<T[]>([]);
  protected breadcrumbs = signal<StandardListEntity[]>([]);

  protected titleSliding: Signal<string> = computed(() => {
    if (this.breadcrumbs().length > 0) {
      return this.breadcrumbs().at(-1).getLabel();
    }
    return this.label() || DEFAULT_SLIDING_MENU_TITLE;
  });

  protected backAction = computed(() => {
    const backAction = new StandardActionBuilder('back', 'chevron-left')
      .setAlign('left')
      .setText(BACK_ACTION_TEXT)
      .build();

    if (this.breadcrumbs().length > 1) {
      const length = Math.round(window.innerWidth / PIXEL_BY_CHARACTER);
      const label = this.breadcrumbs().at(-2).getLabel();

      const text =
        label.length > length ? `${label.substring(0, length)}...` : label;
      backAction.text = text;
    }

    if (this.breadcrumbs().length === 1) {
      backAction.text = this.label();
    }

    return backAction;
  });

  protected isSearchActive = signal<boolean>(false);
  protected currentSearch = signal<string>('');

  protected actionsSliding = computed(() => {
    const actions = [];
    actions.push(this.backAction());
    if (this.withSearch()) {
      actions.push(SEARCH_ACTION);
    }
    if (this.isMultiple() && this.currentValue().length > 0) {
      actions.push(OK_ACTION);
    }
    return actions;
  });

  protected displayedItems = computed(() => {
    const groups = this.breadcrumbs();
    if (groups.length > 0) {
      return groups.at(-1).items;
    }
    if (this.currentSearch()) {
      return this.items()
        .map((item) => item.flat())
        .flat()
        .filter((item) => item.search(this.currentSearch()));
    }
    return this.items();
  });

  public currentValue = signal<T[]>([]);
  protected additionalActions: Signal<StandardAction[]> = computed(() => {
    const actions = [];
    if (!this.isMandatory() && this.isFilled()) {
      actions.push(new StandardAction('clear', 'times'));
    }
    actions.push(new StandardAction('open', 'chevron-right'));
    return actions;
  });
  protected isFilled = computed(() => {
    if (!this.currentValue()) {
      return false;
    }
    return this.currentValue().length > 0;
  });

  public clear(): void {
    this.items().forEach((item) => {
      item.uncheckItem();
    });
    this.currentValue.set([]);
  }

  protected override lastValueEmitted = signal<string>('');

  @ViewChild('list') list: ElementRef<HTMLDivElement>;
  @ViewChild(SlidingMenuComponent) sliding: SlidingMenuComponent;
  @ViewChild(InputSearchComponent)
  search: InputSearchComponent<StandardListEntity>;

  override ngOnChanges(changes: SimpleChanges) {
    if (Object.hasOwn(changes, 'src$')) {
      this.load();
    }
    if (Object.hasOwn(changes, 'defaultValue') && this.defaultValue()) {
      // We will set currentValue after load
      this.checkValue();
    }
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  private load(): void {
    this.subs.push(
      this.src$().subscribe({
        next: (items) => {
          const defaultValue: string[] = [
            ...this._defaultValueString(),
            ...this._defaultValueT().map((v) => v.getId()),
          ];
          items
            .map((item) => item.flat())
            .flat()
            .forEach((item) => {
              if (defaultValue.includes(item.getId())) {
                item.checkItem();
              }
            });
          this.items.set(items);
          this.updateDisplayValue();
        },
      }),
    );
  }

  protected onActionClickSliding($event: ActionEvent) {
    switch ($event.action.name) {
      case 'back':
        this.backGroup();
        break;
      case 'search':
        this.toggleSearch(($event.action as SwitchAction<any>).isActive);
        break;
      case 'ok':
        this.close();
        break;
      case 'cancel':
        this.close();
        break;
    }
  }

  protected onItemChange($event: StandardListEntity) {
    if (!this.isMultiple()) {
      this.items().forEach((item) => item.uncheckItem());
      $event.toggleCheckState(CheckState.check);
      this.close();
    }
    this.updateDisplayValue();
  }

  protected onClickGroup($event: StandardListEntity) {
    this.breadcrumbs.update((breadcrumb) => {
      const result = [];
      result.push(...breadcrumb, $event);
      return result;
    });
    this.animateList('fromRight');
    this.updateDisplayValue();
  }

  private backGroup() {
    if (this.breadcrumbs().length === 0) {
      this.close();
      return;
    }
    this.breadcrumbs.update((breadcrumb) => {
      const result = [];
      result.push(...breadcrumb);
      result.pop();
      return result;
    });
    this.animateList('fromLeft');
  }

  private updateDisplayValue() {
    const items: T[] = [];
    this.items().forEach((item) => {
      items.push(...(item.getCheckedItems() as T[]));
    });
    this.currentValue.set(items);
    this.emit();
  }

  protected onSwipeEnd($event: ScreenSwipeDirection) {
    if ($event.direction === 'right') {
      this.backGroup();
    }
  }

  protected onUpdateSearch($event: string) {
    this.breadcrumbs.set([]);
    this.currentSearch.set($event);
  }

  public override focus(): void {
    this.sliding.open();
  }

  private close(): void {
    this.breadcrumbs.set([]);
    this.isSearchActive.set(false);
    SEARCH_ACTION.isActive = false;
    this.currentSearch.set('');
    this.sliding.close();
  }

  private toggleSearch(isOpen: boolean): void {
    this.isSearchActive.set(isOpen);
    if (this.isSearchActive()) {
      setTimeout(() => {
        this.search.focus();
      }, 150);
      return;
    }
    this.currentSearch.set('');
  }

  private animateList(direction: 'fromRight' | 'fromLeft'): void {
    const translateX = direction === 'fromRight' ? '100%' : '-100%';
    this.list.nativeElement.style.transform = `translateX(${translateX})`;
    setTimeout(() => {
      this.list.nativeElement.style.transition = `transform ${ANIMATION_DURATION}ms`;
      this.list.nativeElement.style.transform = 'translateX(0)';
    }, 50);
    setTimeout(() => {
      this.list.nativeElement.style.transition = '';
    }, ANIMATION_DURATION + 50);
  }

  private emit() {
    const stringIds = this.currentValue()
      .map((v) => v.getId())
      .join(',');
    if (stringIds === this.lastValueEmitted()) {
      return;
    }
    this.lastValueEmitted.set(stringIds);
    this.update.emit(this.currentValue());
  }
}
